import { OnChanges, Component, OnDestroy, IterableDiffers } from '@angular/core';
import * as _ from 'lodash';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { ScheduleRequestResult } from '../../core/models/view-models/schedule-models/schedule-request-result.type';
import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';
import { UIService } from '../../core/services/utils/ui.service';
import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { ScreenContentStatus } from '../../core/enums/screen-status.enum';
import { SchedulesService } from '../../core/services/http-services/schedules.service';
import { PlayerService } from '../../core/services/http-services/player.service';
import { ScheduleRequestParameters } from '../../core/models/view-models/schedule-models/schedule-request-parameters.type';
import { ScheduleCalendarMode } from '../../core/enums/schedule-calendar-mode.enum';
import { ScheduleSettingsDialogService } from '../../core/services/dialog-services/schedule-dialog.service';
import { PlaylistsService } from '../../core/services/http-services/playlists.service';

@Component({
  selector: 'playboard',
  templateUrl: './playboard.component.html',
  styleUrls: ['./playboard.component.scss']
})

export class PlayboardComponent implements OnChanges, OnDestroy {

  headerHeight = '56px';
  scheduleCalendarModes = ScheduleCalendarMode;
  scheduleCalendarMode = ScheduleCalendarMode.Week;
  subscriptions: Subscription = new Subscription();

  selectedScreens: BehaviorScreen[] = [];
  scheduleItems: ScheduleRequestResult = null;

  constructor(public iterableDiffers: IterableDiffers,
    public dataService: SchedulesService,
    public scheduleDialog: ScheduleSettingsDialogService,
    public screenStateService: ScreensStateService,
    public uiService: UIService,
    public playerService: PlayerService,
    private playlistDataService: PlaylistsService
  ) {
    this.subscriptions.add(this.screenStateService.someSchedulesUpdated.subscribe(
      () => this.reloadSchedules()
    ));

    this.subscriptions.add(this.playlistDataService.playlistCollectionChanged.subscribe(
      () => this.reloadSchedules()
    ));

    this.subscriptions.add(
      this.screenStateService.selectedScreensChanged.pipe(
        debounceTime(0)
      )
      .subscribe((data: BehaviorScreen[]) => {
        this.selectedScreens = data;
        if (data && data.length) {
          this.reloadSchedules();
        } else {
          this.scheduleItems = null;
        }
      }));
  }

  get showPlay(): boolean {
    return this.selectedScreens.length && _.every(this.selectedScreens, (screen: BehaviorScreen) =>
      screen.ScreenState && screen.ScreenState.ScreenContentStatus === ScreenContentStatus.Waiting) &&
      this.selectedScreens.some(screen => screen.AssignedPlaylist);
  }

  get showStop(): boolean {
    return this.selectedScreens.length &&
      _.some(this.selectedScreens, (screen: BehaviorScreen) =>
        screen.ScreenState && screen.ScreenState.ScreenContentStatus === ScreenContentStatus.Playing);
  }

  get showPendingSpinner(): boolean {
    return this.selectedScreens.length && _.some(this.selectedScreens, (screen: BehaviorScreen) =>
      screen.ScreenState && screen.ScreenState.ScreenContentStatus === ScreenContentStatus.Pending);
  }

  ngOnChanges(): void {
    this.reloadSchedules();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onModeChange(): void {
    this.reloadSchedules();
  }

  playAll(): void {
    if (this.selectedScreens && this.selectedScreens.length) {
      const screensWithPlaylistAssigned = this.selectedScreens.filter(screen => screen.AssignedPlaylist);
      this.screenStateService.setPendingState(screensWithPlaylistAssigned);

      this.subscriptions.add(
        this.playerService.startAssignedPlaylistOnAllMonitors(_.map(screensWithPlaylistAssigned,
          (screen: BehaviorScreen) => new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress)))
            .subscribe(
            () => {},
            () => this.screenStateService.pendingScreens.next(null))
      );
    }
  }

  stopAll(): void {
    if (this.selectedScreens && this.selectedScreens.length) {
      const screensWithPlaylistAssignedAndPlayingStatus = this.selectedScreens.filter(screen =>
        screen.AssignedPlaylist && screen.ScreenState.ScreenContentStatus === ScreenContentStatus.Playing);

      this.screenStateService.setPendingState(screensWithPlaylistAssignedAndPlayingStatus);

      this.subscriptions.add(
        this.playerService.stopPlaylistOnAllMonitors(_.map(screensWithPlaylistAssignedAndPlayingStatus,
          (screen: BehaviorScreen) => new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress)))
            .subscribe(
            () => {},
            () => this.screenStateService.pendingScreens.next(null))
      );
    }
  }

  onSchedulePanelOpened(): void {
    this.uiService.isSchedulePanelExpanded = true;
    this.reloadSchedules();
  }

  private reloadSchedules(): void {
    if (this.uiService.isSchedulePanelExpanded && this.selectedScreens && this.selectedScreens.length) {
      const parameters: ScheduleRequestParameters = new ScheduleRequestParameters(_.map(this.selectedScreens,
        (screen: BehaviorScreen) => new MonitorKey(screen.MonitorHandler, screen.MachinePhysicalAddress)),
        this.scheduleCalendarMode);

      this.dataService.getSchedulesByScheduleRequest(parameters).subscribe((result: ScheduleRequestResult) => {
        this.scheduleItems = result;
      });
    }
  }
}
